/**
 * What the day looked like the moment something was logged.
 *
 * Called AFTER the write has landed, so one read of the day answers
 * everything: the "before" numbers fall out by subtracting the entry that was
 * just committed, instead of racing a read against the write.
 */

import {
  classifyFoodName,
  computeMood,
  dayEnergy,
  type MealSlot,
  NAMED_LINE_MAX_CHARS,
  NAMED_LINE_RATE,
  type SassContext,
} from '@gramas/shared'
import { activeKcalFor, entriesForDay, goalForDate, totalsFor } from '../db/repo.js'
import { currentProfile } from '../screens/useProfile.js'
import { checkAwards } from '../trophies/detect.js'
import { currentStreak, moodBasisFor, repeatCountThisWeek } from './facts.js'
import { announceAwards } from './toast.js'

/**
 * The day as it stands right now, with nothing being logged.
 *
 * `logContext` answers the same questions but only after a write, because it
 * exists to describe one entry. Her zone needs the standing numbers instead —
 * without them `chipsFor` could never offer the over-budget chips („Ar verta?“,
 * „Blogai?“, „Rytoj“), since their `fits` predicates read `isOver` and
 * `entryKcal` and the zone was handing over neither.
 */
export async function dayContext(date: string): Promise<Partial<SassContext>> {
  const [entries, goal, activeKcal] = await Promise.all([
    entriesForDay(date),
    goalForDate(date),
    activeKcalFor(date),
  ])
  const totals = totalsFor(entries)
  const energy = dayEnergy({ kcalTarget: goal.kcalTarget, loggedKcal: totals.kcal, activeKcal })
  return {
    remainingKcal: energy.remainingKcal,
    kcalTarget: goal.kcalTarget,
    isOver: energy.isOver,
    snackCountToday: entries.filter((entry) => entry.slot === 'snack').length,
    // The day's own biggest entry: what "a big one" means when nothing has
    // just been logged is the largest thing on the page.
    entryKcal: entries.reduce((max, entry) => Math.max(max, entry.kcal), 0),
  }
}

export async function logContext(
  date: string,
  slot: MealSlot | undefined,
  entryKcal: number,
  itemName?: string,
): Promise<Partial<SassContext>> {
  const profile = currentProfile()
  const [entries, goal, activeKcal, basis, streakDays, repeats] = await Promise.all([
    entriesForDay(date),
    goalForDate(date),
    activeKcalFor(date),
    moodBasisFor(date),
    currentStreak(date),
    itemName == null ? Promise.resolve(0) : repeatCountThisWeek(itemName, date),
  ])
  // Fire-and-forget: checking must never add latency to the toast the log
  // itself is about to show. Anything earned announces itself once the scan
  // lands, a beat after the log toast, which is the right order anyway — the
  // entry is the event, the trophy is the consequence.
  void checkAwards(streakDays, profile?.createdAt ?? null).then(announceAwards)
  const totals = totalsFor(entries)
  const energy = dayEnergy({ kcalTarget: goal.kcalTarget, loggedKcal: totals.kcal, activeKcal })
  const budget = energy.kcalTarget + energy.activeKcal
  const snackEntries = entries.filter((entry) => entry.slot === 'snack').length
  const phase = basis.phase

  // The mood a log commit sees. `daysSinceLastLog` is always 0 here — one just
  // landed — so "neglected" can never fire from this call site; that register
  // belongs to the greeting and empty-day surfaces, which pass their own.
  const mood = computeMood({ ...basis, daysSinceLastLog: 0 })

  return {
    slot,
    itemName,
    entryKcal,
    remainingKcal: energy.remainingKcal,
    kcalTarget: goal.kcalTarget,
    isOver: energy.isOver,
    wasOverBefore: totals.kcal - entryKcal > budget,
    // The read already contains this commit, so a snack subtracts itself to
    // give the count the notable trigger is defined against.
    snackCountToday: Math.max(0, slot === 'snack' ? snackEntries - 1 : snackEntries),
    // An unknowable TDEE must not flavour the joke wrongly; no phase, no override.
    phase,
    weightTrendKgWk: basis.weightTrendKgWk,
    // Unlike the snack count this one *includes* the entry just written: "the
    // fourth kebab this week" is counted with the fourth kebab in hand.
    repeatCountWeek: itemName == null ? undefined : repeats,
    foodClass: itemName == null ? null : classifyFoodName(itemName),
    // Whether she may build a line *around* the food's name rather than only
    // reacting to its numbers. Decided here because it needs both the name's
    // length and a coin, and a pool lookup should stay a pure function of its
    // inputs. A supermarket name three lines long is not a punchline.
    namedOk:
      itemName != null &&
      itemName.trim().length > 0 &&
      itemName.trim().length <= NAMED_LINE_MAX_CHARS &&
      Math.random() < NAMED_LINE_RATE,
    mood,
  }
}
